import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { Fragment, memo, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import { Box, Inline, Stack } from '@atlaskit/primitives';
import { SainAction, useAnalytics, useSAINUsageCriteriaMet } from '../common/controller/analytics';
import { ErrorBoundary } from '../common/ui/error-boundary';
import { KnowledgeBadge } from '../knowledge-card-components';
import { RelatedEntityType } from '../related-entities-api';
import { onRelatedEntityClick, onSurfaceShown } from './analytics';
import { messages } from './messages';
import { useTopicBreakElems } from './set-break-point';
import { useRelatedTopics } from './use-related-topics';
var AITopicsByPersonComponent = /*#__PURE__*/memo(function (_ref) {
  var userId = _ref.userId,
    userName = _ref.userName,
    cloudId = _ref.cloudId,
    sessionId = _ref.sessionId,
    fallbackSection = _ref.fallbackSection,
    _ref$v2styles = _ref.v2styles,
    v2styles = _ref$v2styles === void 0 ? false : _ref$v2styles;
  var _useAnalytics = useAnalytics(),
    _useAnalytics2 = _slicedToArray(_useAnalytics, 1),
    fireAnalyticsEvent = _useAnalytics2[0];
  var _useSAINUsageCriteria = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria.fireSAINUsageEvent;
  var _useRelatedTopics = useRelatedTopics(),
    aiRelatedTopics = _useRelatedTopics.aiRelatedTopics,
    fetchRelatedTopics = _useRelatedTopics.fetchRelatedTopics,
    showTopics = _useRelatedTopics.showTopics;
  var wrapperRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showMore = _useState2[0],
    setShowMore = _useState2[1];
  var _useTopicBreakElems = useTopicBreakElems(wrapperRef, showTopics),
    breakElems = _useTopicBreakElems.breakElems;
  var personCardSurfaceEnabled = true;
  var shadowTrafficEnabled = !personCardSurfaceEnabled;
  var onBadgeClickHandler = function onBadgeClickHandler(topicId) {
    fireAnalyticsEvent(onRelatedEntityClick({
      clickedEntityId: topicId,
      clickedEntityType: 'topic',
      principalEntityType: RelatedEntityType.USER,
      principalEntityId: userId,
      sessionId: sessionId,
      location: 'personCard'
    }));
    fireSAINUsageEvent({
      metCriteria: SainAction.TOPIC_CLICKED
    });
  };
  useEffect(function () {
    fetchRelatedTopics({
      entityId: userId,
      principalEntityType: RelatedEntityType.USER,
      cloudId: cloudId
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(function () {
    if (showTopics && !shadowTrafficEnabled) {
      fireAnalyticsEvent(onSurfaceShown({
        principalEntityId: userId,
        principalEntityType: RelatedEntityType.USER,
        relatedTopics: aiRelatedTopics.map(function (topic) {
          return topic.id;
        }),
        sessionId: sessionId,
        location: 'personCard'
      }));
    }
  }, [showTopics, shadowTrafficEnabled, aiRelatedTopics, userId, fireAnalyticsEvent, sessionId]);
  var firstName = userName.split(' ')[0];
  if (shadowTrafficEnabled) {
    return /*#__PURE__*/React.createElement(Fragment, null, fallbackSection);
  }
  return showTopics ? v2styles ? /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Stack, {
    space: "space.075"
  }, /*#__PURE__*/React.createElement(Inline, {
    ref: wrapperRef,
    space: "space.100",
    rowSpace: "space.100",
    alignBlock: "center",
    shouldWrap: true
  }, (breakElems < 0 ? aiRelatedTopics : aiRelatedTopics.slice(0, Math.max(1, breakElems))).map(function (topic) {
    return /*#__PURE__*/React.createElement(KnowledgeBadge, {
      key: topic.id,
      name: topic.name,
      url: "/wiki/topics/".concat(topic.id),
      onBadgeClick: function onBadgeClick() {
        return onBadgeClickHandler(topic.id);
      }
    });
  }), breakElems < aiRelatedTopics.length ? /*#__PURE__*/React.createElement(IconButton, {
    appearance: "subtle",
    onClick: function onClick() {
      return setShowMore(function (s) {
        return !s;
      });
    },
    icon: showMore ? ChevronDownIcon : ChevronRightIcon,
    label: "Expand",
    spacing: "compact"
  }) : null), showMore && /*#__PURE__*/React.createElement(Box, {
    paddingBlockStart: "space.025"
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "space.100",
    shouldWrap: true
  }, aiRelatedTopics.slice(breakElems).map(function (topic) {
    return /*#__PURE__*/React.createElement(KnowledgeBadge, {
      key: topic.id,
      name: topic.name,
      url: "/wiki/topics/".concat(topic.id),
      onBadgeClick: function onBadgeClick() {
        return onBadgeClickHandler(topic.id);
      }
    });
  }))))) : /*#__PURE__*/React.createElement(Box, {
    paddingBlockStart: "space.200"
  }, /*#__PURE__*/React.createElement(Stack, {
    space: "space.100"
  }, /*#__PURE__*/React.createElement(Heading, {
    size: "xxsmall"
  }, /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.ai_topics_surface_header_person, {
    values: {
      userName: firstName
    }
  }))), /*#__PURE__*/React.createElement(Inline, {
    space: "space.100",
    rowSpace: "space.100",
    shouldWrap: true
  }, aiRelatedTopics.map(function (topic) {
    return /*#__PURE__*/React.createElement(KnowledgeBadge, {
      key: topic.id,
      name: topic.name,
      url: "/wiki/topics/".concat(topic.id),
      onBadgeClick: function onBadgeClick() {
        return onBadgeClickHandler(topic.id);
      }
    });
  })))) : null;
});
export var AITopicsByPerson = function AITopicsByPerson(props) {
  return /*#__PURE__*/React.createElement(ErrorBoundary, null, /*#__PURE__*/React.createElement(AITopicsByPersonComponent, props));
};